import moment, { Moment } from "moment";

export interface CalendarEvent {
  id: string;
  start: string | Date;
  end: string | Date;
  state?: string;
}

export interface EventValues {
  startTime?: Date;
  endTime?: Date;
}

/**
 * Checks whether the proposed event (values) overlaps with any existing events.
 *
 * @param events - Array of existing calendar events.
 * @param values - The start/end time of the event to check.
 * @returns true if there is any overlap or if any overlapping event is a "leave" event.
 */
export const checkOverlappingEvents = (
  events: CalendarEvent[] = [],
  values: EventValues,
): boolean => {
  // Use the proposed startTime or now as the reference date for "same-day" filtering
  const referenceDate: Date = values.startTime ?? new Date();

  // Filter events that occur on the same calendar day as the referenceDate
  const eventsForDay = events.filter((event) =>
    moment(referenceDate).isBetween(event.start, event.end, "day", "[]"),
  );

  const eligibleEvents = eventsForDay.filter(
    (e) => e.state?.toLowerCase() !== "rejected"
  );

  // Determine if the proposed event overlaps any existing event time ranges
  const hasOverlap = eligibleEvents.some((event) => {
    const eventStart: Moment = moment(event.start);
    const eventEnd: Moment = moment(event.end);
    const recordStart: Moment = moment(values.startTime);
    const recordEnd: Moment = moment(values.endTime ?? new Date());

    // Overlap if:
    // 1. recordStart is in [eventStart, eventEnd)
    // 2. recordEnd is in (eventStart, eventEnd]
    // 3. record fully encompasses the event (starts before or at eventStart and ends after or at eventEnd)
    return (
      recordStart.isBetween(eventStart, eventEnd, undefined, "[)") ||
      recordEnd.isBetween(eventStart, eventEnd, undefined, "(]") ||
      (recordStart.isSameOrBefore(eventStart) && recordEnd.isSameOrAfter(eventEnd))
    );
  });

  // Also consider it an overlap if any existing event for the day is a "leave" event
  const hasLeaveOverlap = eligibleEvents.some((event) =>
    event.id.startsWith("leave-")
  );

  return hasOverlap || hasLeaveOverlap;
};
